import { styled } from 'styled-components';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from '../ui/Button';
import { darkBlueBackground } from './colors';
import { maxCapNumber } from '../sportConfig';

export enum EditorContent {
  CAP_NUMBERING,
  PLAY_EDITING,
}

const ITEM_HEIGHT = 56;
const VISIBLE_ITEMS = 5;
const PICKER_HEIGHT = ITEM_HEIGHT * VISIBLE_ITEMS;
const MIN_CAP = 1;
// Sport-gated upper bound (waterpolo 14, handball 99), overridable via
// REACT_APP_MAX_CAP_NUMBER.
const MAX_CAP = maxCapNumber;

// Selectable numbers: MIN_CAP..MAX_CAP.
const NUMBERS: (number | null)[] = [
  ...Array.from({ length: MAX_CAP - MIN_CAP + 1 }, (_, i) => i + MIN_CAP),
];

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
`;

const ModalCard = styled.div`
  background: ${(props) => darkBlueBackground};
  border-radius: 20px;
  padding: 24px 20px;
  width: min(320px, 90vw);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
`;

const Title = styled.div`
  color: #fff;
  font-size: 1rem;
  font-weight: 600;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  opacity: 0.7;
`;

const PickerContainer = styled.div`
  position: relative;
  width: 120px;
  height: ${PICKER_HEIGHT}px;
  overflow: hidden;
  -webkit-mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(0, 0, 0, 0.3) 15%,
    black 35%,
    black 65%,
    rgba(0, 0, 0, 0.3) 85%,
    transparent 100%
  );
  mask-image: linear-gradient(
    to bottom,
    transparent 0%,
    rgba(0, 0, 0, 0.3) 15%,
    black 35%,
    black 65%,
    rgba(0, 0, 0, 0.3) 85%,
    transparent 100%
  );
`;

const PickerScroller = styled.div`
  width: 100%;
  height: 100%;
  overflow-y: scroll;
  scroll-snap-type: y mandatory;
  -webkit-overflow-scrolling: touch;

  /* Hide scrollbar */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
`;

const PickerItem = styled.div<{ $active: boolean; $taken: boolean }>`
  height: ${ITEM_HEIGHT}px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  scroll-snap-align: center;
  font-size: ${(props) => (props.$active ? '2.4rem' : '1.6rem')};
  font-weight: ${(props) => (props.$active ? '700' : '400')};
  color: ${(props) =>
    props.$taken
      ? props.$active
        ? '#ff6b6b'
        : '#ff9b9b'
      : props.$active
        ? '#fff'
        : 'rgba(255,255,255,0.35)'};
  transition: all 0.15s ease;
  user-select: none;
  cursor: pointer;
  font-variant-numeric: tabular-nums;
`;

// Small dot marking a number already used by another player in the same team.
const TakenDot = styled.span`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #ff6b6b;
  flex: none;
`;

const PickerSpacer = styled.div`
  height: ${ITEM_HEIGHT * Math.floor(VISIBLE_ITEMS / 2)}px;
`;

const HighlightBar = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 100px;
  height: ${ITEM_HEIGHT}px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.08);
  border: 1px solid rgba(255, 255, 255, 0.12);
  pointer-events: none;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 12px;
  width: 100%;
  justify-content: space-between;
  align-items: center;
`;

const HintText = styled.div`
  color: rgba(255, 255, 255, 0.4);
  font-size: 0.8rem;
  text-align: center;
`;

export const CapNumberingEditor: React.FC<{
  numbering?: (string | null)[] | undefined;
  selectedPlayer?: number | null;
  onSelectCapNumber: (cap: number | null) => () => void;
  // Cap numbers already taken by OTHER players in the same team — highlighted
  // in red so the user can avoid (or knowingly reuse) duplicates.
  usedNumbers?: number[];
}> = ({
  numbering = [],
  selectedPlayer = null,
  onSelectCapNumber,
  usedNumbers = [],
}) => {
  const takenSet = new Set(usedNumbers);
  const isOpen = selectedPlayer !== null;
  const currentCap =
    isOpen && !Number.isNaN(selectedPlayer) && numbering[selectedPlayer!]
      ? parseInt(numbering[selectedPlayer!]!)
      : null;

  const [pickerValue, setPickerValue] = useState<number>(currentCap ?? 1);
  const scrollerRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const scrollTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  // Sync picker when modal opens or player changes
  useEffect(() => {
    if (isOpen) {
      const val = currentCap ?? 1;
      setPickerValue(val);
      // Scroll to the correct position after render
      requestAnimationFrame(() => {
        if (scrollerRef.current) {
          const index = NUMBERS.indexOf(val);
          if (index !== -1) {
            scrollerRef.current.scrollTop = index * ITEM_HEIGHT;
          }
        }
      });
    }
  }, [isOpen, selectedPlayer]);

  const handleScroll = useCallback(() => {
    if (!scrollerRef.current) return;

    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }
    isScrollingRef.current = true;

    scrollTimeoutRef.current = setTimeout(() => {
      isScrollingRef.current = false;
      if (!scrollerRef.current) return;
      const scrollTop = scrollerRef.current.scrollTop;
      const index = Math.round(scrollTop / ITEM_HEIGHT);
      const clampedIndex = Math.max(0, Math.min(index, NUMBERS.length - 1));
      const val = NUMBERS[clampedIndex];
      if (val !== null) {
        setPickerValue(val);
      }
    }, 80);
  }, []);

  const scrollToIndex = useCallback((index: number) => {
    if (!scrollerRef.current) return;
    scrollerRef.current.scrollTo({
      top: index * ITEM_HEIGHT,
      behavior: 'smooth',
    });
  }, []);

  const handleConfirm = useCallback(() => {
    onSelectCapNumber(pickerValue)();
  }, [onSelectCapNumber, pickerValue]);

  const handleRemove = useCallback(() => {
    onSelectCapNumber(null)();
  }, [onSelectCapNumber]);

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // No dedicated close callback, so confirm the current value.
        handleConfirm();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [isOpen, handleConfirm]);

  if (!isOpen) return null;

  return (
    <Overlay>
      <ModalCard onClick={(e) => e.stopPropagation()}>
        <Title>Select player number</Title>

        <PickerContainer>
          <HighlightBar />
          <PickerScroller ref={scrollerRef} onScroll={handleScroll}>
            <PickerSpacer />
            {NUMBERS.map((num) => {
              const isActive = num === pickerValue;
              const isTaken = num !== null && takenSet.has(num);
              return (
                <PickerItem
                  key={num ?? 'remove'}
                  $active={isActive}
                  $taken={isTaken}
                  onClick={() => {
                    if (num !== null) {
                      setPickerValue(num);
                      const index = NUMBERS.indexOf(num);
                      scrollToIndex(index);
                    }
                  }}
                >
                  {num !== null ? num.toString().padStart(2, '0') : '—'}
                  {isTaken && <TakenDot />}
                </PickerItem>
              );
            })}
            <PickerSpacer />
          </PickerScroller>
        </PickerContainer>

        <HintText>
          {takenSet.size > 0
            ? 'Scroll to select · red = already used in this team'
            : 'Scroll to select number'}
        </HintText>

        <ButtonRow>
          <Button secondary={true} onClick={handleRemove}>
            Remove
          </Button>
          <Button onClick={handleConfirm}>OK</Button>
        </ButtonRow>
      </ModalCard>
    </Overlay>
  );
};
